// This file is auto-generated. Do not edit it manually.

import {
  type FeatureResult,
  type GrowthBook,
  // eslint-disable-next-line no-restricted-imports
  useFeatureIsOn as useGbFeatureIsOn,
  // eslint-disable-next-line no-restricted-imports
  useFeatureValue as useGbFeatureValue,
  // eslint-disable-next-line no-restricted-imports
  useGrowthBook as useGbGrowthBook,
} from '@growthbook/growthbook-react'
import {
  subscribeToGrowthBookFeaturesUpdated,
  useGrowthBookDefaultsContext,
} from 'fitify-ui/src/utils/growthbook'
import { useEffect, useMemo, useState } from 'react'

import devGrowthBookDefaults from '../../../config/devGrowthBookDefaults.json'
import productionGrowthBookDefaults from '../../../config/productionGrowthBookDefaults.json'
import stagingGrowthBookDefaults from '../../../config/stagingGrowthBookDefaults.json'

import type { AppDefaultFeatures, AppFeatures } from './types'

export const defaultFeatures: AppDefaultFeatures =
  process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? productionGrowthBookDefaults
    : process.env.NEXT_PUBLIC_APP_ENV === 'staging'
      ? stagingGrowthBookDefaults
      : devGrowthBookDefaults

export const useFeatureIsOn = (id: keyof AppFeatures) =>
  useGbFeatureIsOn<AppFeatures>(id)

export const useFeatureValue = <K extends keyof AppFeatures>(id: K) => {
  const defaultValues = useGrowthBookDefaultsContext<AppDefaultFeatures>()
  return useGbFeatureValue<AppFeatures[K]>(id, defaultValues[id].defaultValue)
}

type EvalFeature<K extends keyof AppFeatures = keyof AppFeatures> = (
  id: K | string
) => FeatureResult<AppFeatures[K] | null> | undefined

export type UseGrowthBook = Pick<
  GrowthBook<AppFeatures & Record<string, unknown>>,
  'isOn' | 'ready' | 'updateAttributes'
> & {
  evalFeature: EvalFeature
}

/**
 * Custom useGrowthBook hook that wraps evalFeature/isOn/updateAttributes with error handling.
 *
 * Subscribes to feature updates emitted by the patched setRenderer in growthbook.tsx.
 * When features change, the subscriber fires, incrementing a local counter that
 * invalidates the useMemo — ensuring fresh evalFeature results propagate.
 */
export const useGrowthBook = (): UseGrowthBook => {
  const growthBook = useGbGrowthBook<AppFeatures>()
  const [featuresVersion, setFeaturesVersion] = useState(0)

  useEffect(() => {
    return subscribeToGrowthBookFeaturesUpdated(() => {
      setFeaturesVersion((v) => v + 1)
    })
  }, [])

  return useMemo(() => {
    const evalFeature: UseGrowthBook['evalFeature'] = (id) => {
      try {
        return growthBook.evalFeature(id as keyof AppFeatures)
      } catch (error) {
        console.error(`Unable to resolve feature ${id}`, error)
        return undefined
      }
    }
    const isOn: UseGrowthBook['isOn'] = (id) => {
      try {
        return growthBook.isOn(id as keyof AppFeatures)
      } catch (error) {
        console.error(`Unable to resolve feature ${id}`, error)
        return false
      }
    }
    const updateAttributes: UseGrowthBook['updateAttributes'] = async (
      ...args
    ) => {
      try {
        return growthBook.updateAttributes(...args)
      } catch (error) {
        console.error('Unable to update attributes', error)
      }
    }
    return {
      evalFeature,
      isOn,
      ready: growthBook.ready,
      updateAttributes,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growthBook, growthBook.ready, featuresVersion])
}
